import type {
  AcknowledgeCircularInput,
  CancelMeetingInput,
  CapaActionListFilter,
  CircularListFilter,
  CloseMeetingInput,
  CloseNonconformityInput,
  CreateCapaActionInput,
  CreateCircularInput,
  CreateMeetingInput,
  CreateMeetingTypeInput,
  CreateNonconformityInput,
  CreateRequestTypeInput,
  CreateRootCauseInput,
  EvaluateNonconformityInput,
  MeetingAttendanceInput,
  MeetingEffectivenessInput,
  MeetingListFilter,
  NcRecurrenceInput,
  NonconformityListFilter,
  PublishCircularInput,
  ReassignActionInput,
  RecordActionExecutionInput,
  SgqIndicatorsQuery,
  SupportTypeListFilter,
  UpdateCapaActionInput,
  UpdateCircularInput,
  UpdateMeetingInput,
  UpdateMeetingTypeInput,
  UpdateNonconformityInput,
  UpdateRequestTypeInput,
  UpdateRootCauseInput,
  VerifyEffectivenessInput,
} from '@qualicloud/contracts';
import { ApiScope, request, requestFile } from './client';
import type {
  CapaActionDetail,
  CapaActionListResponse,
  CapaCaseDetail,
  CircularDetail,
  CircularListResponse,
  MeetingDetail,
  MeetingListResponse,
  MeetingType,
  NonconformityDetail,
  NonconformityListResponse,
  RequestType,
  SgqIndicators,
} from './sgq.types';

/**
 * Cliente HTTP do SGQ — Onda 2.
 *
 * TODO CAMINHO AQUI CONSTA DE `GATEWAY_ROUTE_SURFACE`
 * (`@qualicloud/contracts/gateway-routes`), e é isso que
 * `frontend-web/src/test/api-route-contract.test.ts` verifica chamada a chamada. O
 * gateway roteia POR CAMINHO: um segmento a mais ou a menos devolve 404 antes
 * de chegar ao serviço, e foi assim que 29 dos 43 passos da jornada do GED
 * caíram com o teste verde (`docs/validation/desk-execution.md`, Q1).
 *
 * Os escopos são `ApiScope.CORE`, que é STRING VAZIA: o gateway publica
 * `/nonconformities`, `/meetings`, `/capa/...`, `/circulars` e `/sgq/indicators`
 * na raiz. Prefixar com `/core` recriaria exatamente aquele defeito.
 *
 * TODA rota mutante devolve o AGREGADO atualizado (`NonconformityDetail`,
 * `MeetingDetail`, `CapaActionDetail`, `CapaCaseDetail`, `CircularDetail`) —
 * conferido nos `ms-core/src/modules/<módulo>/<módulo>.service.ts`. As
 * telas invalidam o cache mesmo assim, mas o tipo diz a verdade sobre o que
 * volta.
 *
 * O caminho acima traz `<módulo>` no lugar do glob com asterisco de propósito:
 * um asterisco seguido de barra fecha ESTE bloco de comentário, e o resto do
 * arquivo vira código solto.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Não-conformidade
// ─────────────────────────────────────────────────────────────────────────────

export const nonconformitiesApi = {
  list(filter: Partial<NonconformityListFilter>): Promise<NonconformityListResponse> {
    return request<NonconformityListResponse>({
      scope: ApiScope.CORE,
      path: '/nonconformities',
      query: filter as Record<string, unknown>,
    });
  },

  detail(id: string): Promise<NonconformityDetail> {
    return request<NonconformityDetail>({
      scope: ApiScope.CORE,
      path: `/nonconformities/${id}`,
    });
  },

  /**
   * Abertura de NC.
   *
   * As evidências vão como `evidenceUploadIds` — ids de uploads JÁ finalizados
   * no MEDIA, exatamente como o GED faz. O binário nunca passa pelo CORE. No
   * legado a falha de upload NÃO bloqueava a criação da NC (§A.6): a NC nascia
   * sem a evidência e o usuário via apenas um aviso de sessão.
   */
  create(input: CreateNonconformityInput): Promise<NonconformityDetail> {
    return request<NonconformityDetail>({
      scope: ApiScope.CORE,
      path: '/nonconformities',
      method: 'POST',
      body: input,
    });
  },

  /** Correção antes da avaliação. NÃO é ato assinado — plano §5.3. */
  update(id: string, input: UpdateNonconformityInput): Promise<NonconformityDetail> {
    return request<NonconformityDetail>({
      scope: ApiScope.CORE,
      path: `/nonconformities/${id}`,
      method: 'PATCH',
      body: input,
    });
  },

  /** Vínculo de reincidência. Informativo — não dispara nada (§A.4). */
  addRecurrences(id: string, input: NcRecurrenceInput): Promise<NonconformityDetail> {
    return request<NonconformityDetail>({
      scope: ApiScope.CORE,
      path: `/nonconformities/${id}/recurrences`,
      method: 'POST',
      body: input,
    });
  },

  /** Ato ASSINADO — `NC_EVALUATE` (procede) ou `NC_NOT_APPLICABLE` (não procede). */
  evaluate(id: string, input: EvaluateNonconformityInput): Promise<NonconformityDetail> {
    return request<NonconformityDetail>({
      scope: ApiScope.CORE,
      path: `/nonconformities/${id}/evaluation`,
      method: 'POST',
      body: input,
    });
  },

  /**
   * Ato ASSINADO — `NC_CLOSE`.
   *
   * Não existe parâmetro que force o encerramento: a completude do CAPA é
   * invariante da transição no servidor, e a tela mostra `closureBlockers`
   * antes de oferecer o botão.
   */
  close(id: string, input: CloseNonconformityInput): Promise<NonconformityDetail> {
    return request<NonconformityDetail>({
      scope: ApiScope.CORE,
      path: `/nonconformities/${id}/close`,
      method: 'POST',
      body: input,
    });
  },

  /**
   * PDF consolidado — §E.4, o `pdf()` de `qualidade.php:742-858`.
   *
   * Traz identificação, GUT do emissor E do gestor, avaliação, reincidências,
   * Ishikawa, plano 5W2H e o manifesto de assinaturas (§11.50(b)). Devolve
   * bytes, não bilhete: o relatório não é registro controlado do MEDIA e não
   * ocupa retenção WORM.
   */
  exportPdf(id: string): Promise<{ blob: Blob; fileName: string }> {
    return requestFile({
      scope: ApiScope.CORE,
      path: `/nonconformities/${id}/export/pdf`,
      fallbackFileName: 'nao-conformidade.pdf',
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Catálogos administráveis — tipo de reunião e tipo de solicitação
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normaliza a lista dos catálogos.
 *
 * O CORE devolve um array simples, mas o restante do produto usa dois
 * invólucros diferentes para coleções (`{ items }` na grade paginada,
 * `{ departments }` no diretório). Aceitar as duas formas aqui custa uma
 * função e evita que a tela de administração quebre com "types.map is not a
 * function" se o DTO ganhar um envelope — que é exatamente o tipo de
 * divergência silenciosa que este cliente já pagou caro
 * (`documentsApi.counts`, mesmo remédio).
 *
 * Não valida campo a campo de propósito: a checagem de forma é do
 * `responseSchema` do `request`, quando houver; aqui só se resolve o invólucro.
 */
function unwrapList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object' && Array.isArray((payload as { items?: unknown }).items)) {
    return (payload as { items: T[] }).items;
  }
  return [];
}

/**
 * Catálogo de tipos de reunião.
 *
 * `list` é aberto a QUALQUER sessão autenticada — é o combo do formulário de
 * agendamento, não uma tela de administração. Só a escrita exige
 * `sgq-type:manage`.
 *
 * Chamar sem `includeInactive` devolve só os tipos ATIVOS, que é o que um
 * formulário deve oferecer. A tela de administração passa
 * `includeInactive: true`: sem isso, desativar um tipo o faria sumir da própria
 * tela onde se desativa, e não haveria como reativá-lo.
 */
export const meetingTypesApi = {
  async list(params?: Partial<SupportTypeListFilter>): Promise<MeetingType[]> {
    const payload = await request<unknown>({
      scope: ApiScope.CORE,
      path: '/meeting-types',
      query: params as Record<string, unknown> | undefined,
    });
    return unwrapList<MeetingType>(payload);
  },

  create(input: CreateMeetingTypeInput): Promise<MeetingType> {
    return request<MeetingType>({
      scope: ApiScope.CORE,
      path: '/meeting-types',
      method: 'POST',
      body: input,
    });
  },

  /**
   * Edição do tipo.
   *
   * O `code` NÃO entra: `updateMeetingTypeSchema` o omite porque ele viaja
   * congelado para dentro da reunião e do payload assinado da ata. Renomear é
   * permitido inclusive em tipo do sistema — o nome é vocabulário, e mudá-lo
   * não muda regra nenhuma.
   */
  update(id: string, input: UpdateMeetingTypeInput): Promise<MeetingType> {
    return request<MeetingType>({
      scope: ApiScope.CORE,
      path: `/meeting-types/${id}`,
      method: 'PATCH',
      body: input,
    });
  },

  /**
   * Exclusão.
   *
   * Recusada com 409 quando o tipo é do sistema (`SGQ.TYPE_SYSTEM_IMMUTABLE`) ou
   * quando há reunião usando-o (`MEETING.TYPE_IN_USE`). A tela deve olhar
   * `system` e `usageCount` ANTES e oferecer "Desativar" no lugar: desativar
   * tira o tipo dos formulários e preserva o histórico que o cita.
   */
  async remove(id: string): Promise<void> {
    await request({ scope: ApiScope.CORE, path: `/meeting-types/${id}`, method: 'DELETE' });
  },
};

/** Catálogo de tipos de solicitação. Mesmas regras do catálogo de reunião. */
export const requestTypesApi = {
  async list(params?: Partial<SupportTypeListFilter>): Promise<RequestType[]> {
    const payload = await request<unknown>({
      scope: ApiScope.CORE,
      path: '/request-types',
      query: params as Record<string, unknown> | undefined,
    });
    return unwrapList<RequestType>(payload);
  },

  create(input: CreateRequestTypeInput): Promise<RequestType> {
    return request<RequestType>({
      scope: ApiScope.CORE,
      path: '/request-types',
      method: 'POST',
      body: input,
    });
  },

  update(id: string, input: UpdateRequestTypeInput): Promise<RequestType> {
    return request<RequestType>({
      scope: ApiScope.CORE,
      path: `/request-types/${id}`,
      method: 'PATCH',
      body: input,
    });
  },

  /** Recusa em 409: `SGQ.TYPE_SYSTEM_IMMUTABLE` ou `REQUEST.TYPE_IN_USE`. */
  async remove(id: string): Promise<void> {
    await request({ scope: ApiScope.CORE, path: `/request-types/${id}`, method: 'DELETE' });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Reunião
// ─────────────────────────────────────────────────────────────────────────────

export const meetingsApi = {
  list(filter: Partial<MeetingListFilter>): Promise<MeetingListResponse> {
    return request<MeetingListResponse>({
      scope: ApiScope.CORE,
      path: '/meetings',
      query: filter as Record<string, unknown>,
    });
  },

  detail(id: string): Promise<MeetingDetail> {
    return request<MeetingDetail>({ scope: ApiScope.CORE, path: `/meetings/${id}` });
  },

  create(input: CreateMeetingInput): Promise<MeetingDetail> {
    return request<MeetingDetail>({
      scope: ApiScope.CORE,
      path: '/meetings',
      method: 'POST',
      body: input,
    });
  },

  /**
   * Edição da reunião.
   *
   * ⚠️ OMITIR `nonconformityIds` PRESERVA os vínculos. Ausente significa "não
   * mexi nisso", nunca "esvazie" — no legado, `updateReuniao` sem `nc[]`
   * chamava `syncNcToReuniao(id, array())` e apagava TODOS os vínculos de NC
   * em silêncio (BUG-REU-07). A guarda mora no schema; esta assinatura só não
   * a contraria mandando array vazio por conta própria.
   */
  update(id: string, input: UpdateMeetingInput): Promise<MeetingDetail> {
    return request<MeetingDetail>({
      scope: ApiScope.CORE,
      path: `/meetings/${id}`,
      method: 'PATCH',
      body: input,
    });
  },

  /** Confirma, recusa ou registra presença. `userId` ausente = o próprio ator. */
  setAttendance(id: string, input: MeetingAttendanceInput): Promise<MeetingDetail> {
    return request<MeetingDetail>({
      scope: ApiScope.CORE,
      path: `/meetings/${id}/attendance`,
      method: 'POST',
      body: input,
    });
  },

  /** Abertura EXPLÍCITA. No legado o estado vinha do relógio. */
  open(id: string): Promise<MeetingDetail> {
    return request<MeetingDetail>({
      scope: ApiScope.CORE,
      path: `/meetings/${id}/open`,
      method: 'POST',
      body: {},
    });
  },

  /** Ato ASSINADO — `MEETING_CLOSE`. Emite a ata. */
  close(id: string, input: CloseMeetingInput): Promise<MeetingDetail> {
    return request<MeetingDetail>({
      scope: ApiScope.CORE,
      path: `/meetings/${id}/close`,
      method: 'POST',
      body: input,
    });
  },

  /**
   * Eficácia da SACAP. Justificativa obrigatória nos DOIS caminhos.
   *
   * Este endpoint NÃO cria SACAP de follow-up: o follow-up automático em D+3 é
   * o de `POST /capa/actions/:id/effectiveness` (§B.8).
   */
  recordEffectiveness(id: string, input: MeetingEffectivenessInput): Promise<MeetingDetail> {
    return request<MeetingDetail>({
      scope: ApiScope.CORE,
      path: `/meetings/${id}/effectiveness`,
      method: 'POST',
      body: input,
    });
  },

  /**
   * Cancelamento com justificativa obrigatória — §B.1 `cancelar()`/`justifica()`.
   *
   * Não é ato assinado: cancelar afirma que a análise crítica NÃO vai
   * acontecer, e o que muda status regulatório é o ENCERRAMENTO, que emite ata.
   * Os participantes são notificados — o legado gravava a data e não avisava
   * ninguém.
   */
  cancel(id: string, input: CancelMeetingInput): Promise<MeetingDetail> {
    return request<MeetingDetail>({
      scope: ApiScope.CORE,
      path: `/meetings/${id}/cancel`,
      method: 'POST',
      body: input,
    });
  },

  /** Ata em PDF com manifesto de assinaturas — §B.9, o `relatorioPdf()`. */
  exportPdf(id: string): Promise<{ blob: Blob; fileName: string }> {
    return requestFile({
      scope: ApiScope.CORE,
      path: `/meetings/${id}/export/pdf`,
      fallbackFileName: 'ata-reuniao.pdf',
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// CAPA — Ishikawa e SOLICITAÇÕES (o antigo "plano de ação 5W2H")
//
// O caminho HTTP continua `/capa/actions` porque é o que o gateway publica; o
// que mudou é o vocabulário do produto e a origem, que virou opcional.
// ─────────────────────────────────────────────────────────────────────────────

export const capaApi = {
  /** Caso CAPA: Ishikawa agrupado por categoria e o plano de ação inteiro. */
  case(capaCaseId: string): Promise<CapaCaseDetail> {
    return request<CapaCaseDetail>({
      scope: ApiScope.CORE,
      path: `/capa/cases/${capaCaseId}`,
    });
  },

  /** A causa pode nascer dentro de uma reunião: o Ishikawa acontece na SACAP. */
  createRootCause(
    capaCaseId: string,
    input: CreateRootCauseInput & { readonly meetingId?: string | null },
  ): Promise<CapaCaseDetail> {
    return request<CapaCaseDetail>({
      scope: ApiScope.CORE,
      path: `/capa/cases/${capaCaseId}/root-causes`,
      method: 'POST',
      body: input,
    });
  },

  /**
   * Edita ou REPRIORIZA a causa.
   *
   * É o endpoint que o legado não tinha: `sort_order` era gravado literal `0` e
   * nada o atualizava, então o `ORDER BY tipo, sort_order` degenerava em ordem
   * alfabética de categoria e arrastar a causa não fazia nada (BUG-REU-09).
   */
  updateRootCause(rootCauseId: string, input: UpdateRootCauseInput): Promise<CapaCaseDetail> {
    return request<CapaCaseDetail>({
      scope: ApiScope.CORE,
      path: `/capa/root-causes/${rootCauseId}`,
      method: 'PATCH',
      body: input,
    });
  },

  /** Recusado quando há ação vinculada — o legado deixava a ação órfã de causa. */
  deleteRootCause(rootCauseId: string): Promise<CapaCaseDetail> {
    return request<CapaCaseDetail>({
      scope: ApiScope.CORE,
      path: `/capa/root-causes/${rootCauseId}`,
      method: 'DELETE',
    });
  },

  listActions(filter: Partial<CapaActionListFilter>): Promise<CapaActionListResponse> {
    return request<CapaActionListResponse>({
      scope: ApiScope.CORE,
      path: '/capa/actions',
      query: filter as Record<string, unknown>,
    });
  },

  action(actionId: string): Promise<CapaActionDetail> {
    return request<CapaActionDetail>({
      scope: ApiScope.CORE,
      path: `/capa/actions/${actionId}`,
    });
  },

  /**
   * Criação de SOLICITAÇÃO — inclusive a avulsa.
   *
   * `requestTypeId` é obrigatório e `rootCauseId` é opcional: a solicitação sem
   * causa é a que o legado sempre teve e a Onda 2 fechou. Quem exige a causa é
   * o tipo (`requiresRootCause`), e a recusa vem do servidor como
   * `CAPA.NO_ROOT_CAUSE`; quem exige "por quê" e "como" é `requiresFullPlan`.
   * Nada disso é decidido aqui — o cliente não conhece as flags do tipo até
   * carregar o catálogo.
   */
  createAction(input: CreateCapaActionInput): Promise<CapaActionDetail> {
    return request<CapaActionDetail>({
      scope: ApiScope.CORE,
      path: '/capa/actions',
      method: 'POST',
      body: input,
    });
  },

  updateAction(actionId: string, input: UpdateCapaActionInput): Promise<CapaActionDetail> {
    return request<CapaActionDetail>({
      scope: ApiScope.CORE,
      path: `/capa/actions/${actionId}`,
      method: 'PATCH',
      body: input,
    });
  },

  /** Diário de bordo. `transition` move o estado; `null` apenas anota. */
  recordExecution(
    actionId: string,
    input: RecordActionExecutionInput,
  ): Promise<CapaActionDetail> {
    return request<CapaActionDetail>({
      scope: ApiScope.CORE,
      path: `/capa/actions/${actionId}/executions`,
      method: 'POST',
      body: input,
    });
  },

  reassign(actionId: string, input: ReassignActionInput): Promise<CapaActionDetail> {
    return request<CapaActionDetail>({
      scope: ApiScope.CORE,
      path: `/capa/actions/${actionId}/reassign`,
      method: 'POST',
      body: input,
    });
  },

  /**
   * Ato ASSINADO — `EFFECTIVENESS_VERIFY` ou `EFFECTIVENESS_REJECT`.
   *
   * REPROVAR NÃO É SÓ REPROVAR: o servidor cria, na MESMA transação, uma SACAP
   * de follow-up em D+3 às 09:00 no fuso da organização, herda NCs e
   * participantes, move a ação para a nova reunião e a reabre. No legado isso
   * acontecia em silêncio — e dois dos sete passos falhavam sempre, sem
   * ninguém saber (BUG-REU-02, BUG-REU-03). A tela AVISA antes de confirmar.
   */
  verifyEffectiveness(
    actionId: string,
    input: VerifyEffectivenessInput,
  ): Promise<CapaActionDetail> {
    return request<CapaActionDetail>({
      scope: ApiScope.CORE,
      path: `/capa/actions/${actionId}/effectiveness`,
      method: 'POST',
      body: input,
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Circular
// ─────────────────────────────────────────────────────────────────────────────

export const circularsApi = {
  list(filter: Partial<CircularListFilter>): Promise<CircularListResponse> {
    return request<CircularListResponse>({
      scope: ApiScope.CORE,
      path: '/circulars',
      query: filter as Record<string, unknown>,
    });
  },

  /**
   * Detalhe com a matriz de leitura.
   *
   * ABRIR ESTA TELA NÃO REGISTRA CIÊNCIA. No legado a leitura era gravada no
   * simples ato de abrir a circular, e `setRead` sobrescrevia o carimbo a cada
   * reabertura — o sistema guardava a ÚLTIMA leitura em vez da primeira
   * (BUG-CIR-01). A ciência é ato deliberado e assinado.
   */
  detail(id: string): Promise<CircularDetail> {
    return request<CircularDetail>({ scope: ApiScope.CORE, path: `/circulars/${id}` });
  },

  /** Nasce em RASCUNHO: erro de digitação é corrigível antes de virar evidência. */
  create(input: CreateCircularInput): Promise<CircularDetail> {
    return request<CircularDetail>({
      scope: ApiScope.CORE,
      path: '/circulars',
      method: 'POST',
      body: input,
    });
  },

  /**
   * Corrige o RASCUNHO — BUG-CIR-07.
   *
   * O estado `DRAFT` foi criado com a justificativa de "corrigir erro de
   * digitação antes de virar evidência" e não entregava saída nenhuma além de
   * publicar: o rascunho era tão imutável quanto a publicação.
   *
   * ⚠️ Mesma guarda anti-wipe da reunião: OMITIR os campos de destinatário
   * PRESERVA a lista. Mandar array vazio a esvazia.
   */
  update(id: string, input: UpdateCircularInput): Promise<CircularDetail> {
    return request<CircularDetail>({
      scope: ApiScope.CORE,
      path: `/circulars/${id}`,
      method: 'PATCH',
      body: input,
    });
  },

  /**
   * Descarta o RASCUNHO nunca publicado — a terceira saída de `DRAFT`.
   *
   * O NÚMERO não volta à sequência: numeração é identidade, e reutilizá-la faria
   * duas circulares diferentes carregarem o mesmo código. Publicada, a circular
   * não se exclui — o banco recusa.
   */
  discard(id: string): Promise<void> {
    return request<void>({
      scope: ApiScope.CORE,
      path: `/circulars/${id}`,
      method: 'DELETE',
    });
  },

  /** Só o AUTOR publica. A partir daqui o texto é imutável. */
  publish(id: string, input: PublishCircularInput): Promise<CircularDetail> {
    return request<CircularDetail>({
      scope: ApiScope.CORE,
      path: `/circulars/${id}/publish`,
      method: 'POST',
      body: input,
    });
  },

  /** Ato ASSINADO — `CIRCULAR_ACKNOWLEDGE`. A primeira ciência é imutável. */
  acknowledge(id: string, input: AcknowledgeCircularInput): Promise<CircularDetail> {
    return request<CircularDetail>({
      scope: ApiScope.CORE,
      path: `/circulars/${id}/acknowledge`,
      method: 'POST',
      body: input,
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Indicadores
// ─────────────────────────────────────────────────────────────────────────────

export const sgqIndicatorsApi = {
  /**
   * Insumo da ANÁLISE CRÍTICA PELA DIREÇÃO — ISO 13485 §5.6.
   *
   * Nenhum destes indicadores existia no legado (§E.6). O painel que existia
   * zerava dois dos quatro baldes de NC (BUG-IND-03) e, no de tarefas, somava
   * baldes que se sobrepunham — o total ficava maior que o número real de
   * tarefas (BUG-IND-01).
   */
  query(query: Partial<SgqIndicatorsQuery>): Promise<SgqIndicators> {
    return request<SgqIndicators>({
      scope: ApiScope.CORE,
      path: '/sgq/indicators',
      query: query as Record<string, unknown>,
    });
  },

  /**
   * Exportação — outro item que §E.6 lista como INEXISTENTE no legado.
   *
   * O arquivo é montado no SERVIDOR, e não no navegador: a folha levada à
   * análise crítica pela direção precisa de um evento `EXPORT` na trilha
   * dizendo quem a emitiu, com qual recorte e qual artefato saiu. Um `Blob`
   * construído no cliente não deixa rastro nenhum.
   */
  exportReport(
    query: Partial<SgqIndicatorsQuery>,
    format: 'csv' | 'pdf',
  ): Promise<{ blob: Blob; fileName: string }> {
    return requestFile({
      scope: ApiScope.CORE,
      path: '/sgq/indicators/export',
      query: { ...query, format } as Record<string, unknown>,
      fallbackFileName: `indicadores-sgq.${format}`,
    });
  },
};
